// Internal helpers shared by drawer modules.
// Not exported from index.ts — drawer-module-only.

import React from 'react'
import type { CircleDot } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TONE_HEX } from '@/lib/phaseTone'
import type { Tone } from './types'

// ─── Module tone (lib/phaseTone) ─────────────────────────────────────────────
//
// A module takes the pastel of the phase it represents (quote peach, job sky,
// invoice butter / paid mint / overdue blush). Modules that aren't a phase of
// work — activity, people, documents, photos — are neutral glass. The chip and
// hairline read --c / --c-t from the `.tn-<tone>` class (index.css).

export type ModuleTone = Tone | 'neutral'

/** Solid colour per module tone, for icons and bars that take a plain value. */
export const TONE_DOT_COLOR: Record<ModuleTone, string> = {
  ...TONE_HEX,
  neutral: 'rgb(var(--fg-rgb) / 0.60)',
}

// ─── SectionHeader ─────────────────────────────────────────────────────────────
//
// Used by module panels — 28×28 pastel icon chip + title + optional meta.
// Replaces the legacy TONE_CHIP class-based header.

export function SectionHeader({ tone, icon: Icon, title, meta }: {
  tone: ModuleTone; icon: typeof CircleDot; title: string; meta?: React.ReactNode
}) {
  // Meta sits on its own muted line under the title (UX-036): side by side,
  // "QT-2026-002 · sent Oct 14" squeezed the title and clipped on phones.
  return (
    <div className="flex items-start gap-2.5 mb-3 min-w-0">
      <span
        className={`tn-${tone}`}
        style={{
          width: 28,
          height: 28,
          borderRadius: 9999,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--c-t)',
          color: 'var(--c)',
          flexShrink: 0,
        }}
      >
        <Icon style={{ width: 13, height: 13 }} />
      </span>
      <div className="min-w-0 flex-1" style={{ paddingTop: meta ? 0 : 4 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.012em', color: 'rgb(var(--fg-rgb))', lineHeight: 1.3 }}>
          {title}
        </h3>
        {meta && (
          <div style={{ fontSize: 13, color: 'var(--midnight-text3)', marginTop: 2, overflowWrap: 'anywhere' }}>
            {meta}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── ModulePanel ───────────────────────────────────────────────────────────────
//
// §10 glass nesting: inner surface inside DetailDrawer uses ds-tile-inset
// (translucent dark, NO backdrop-filter) — not a nested glass blur.

export function ModulePanel({ children, accent }: { children: React.ReactNode; accent: ModuleTone }) {
  return (
    <div
      className={cn('relative overflow-hidden')}
      style={{
        borderRadius: 'var(--midnight-r-lg, 24px)',
        background: 'rgb(var(--bg-rgb) / 0.25)',          // ds-tile-inset: no backdrop-filter
        border: '0.5px solid var(--midnight-border, rgb(var(--fg-rgb) / 0.08))',
        padding: 20,
      }}
    >
      {/* Top-edge hairline accent in the module's tone */}
      <div
        aria-hidden="true"
        className={`tn-${accent}`}
        style={{
          position: 'absolute',
          top: 0,
          left: 16,
          right: 16,
          height: 1,
          background: 'var(--c)',
          opacity: 0.35,
          pointerEvents: 'none',
        }}
      />
      {children}
    </div>
  )
}

// ─── GhostButton ───────────────────────────────────────────────────────────────
//
// Secondary glass pill button used in module action rows.
// Replaces the legacy ghost/outline button with MIDNIGHT secondary style.

export function GhostButton({ icon: Icon, label, onClick }: {
  icon: typeof CircleDot; label: string; onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        minHeight: 44,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: '0 14px',
        fontSize: 13,
        fontWeight: 500,
        color: 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))',
        background: 'rgb(var(--fg-rgb) / 0.06)',
        border: '0.5px solid rgb(var(--fg-rgb) / 0.10)',
        borderRadius: 9999,
        cursor: 'pointer',
        transition: 'background 0.2s ease, color 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgb(var(--fg-rgb) / 0.10)'
        e.currentTarget.style.color = 'rgb(var(--fg-rgb))'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgb(var(--fg-rgb) / 0.06)'
        e.currentTarget.style.color = 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))'
      }}
    >
      <Icon style={{ width: 14, height: 14, flexShrink: 0 }} />
      {label}
    </button>
  )
}
